import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Check, X, Sparkle, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Pricing() {
    const { user } = useAuth();
    const [msg, setMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const upgrade = async () => {
        setMsg(""); setLoading(true);
        try {
            const { data } = await api.post("/subscription/checkout");
            if (data?.url) window.location.href = data.url;
        } catch (e) {
            setMsg(formatApiError(e));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-white" data-testid="pricing-page">
            <Navbar />
            <section className="border-b border-zinc-200">
                <div className="mx-auto max-w-7xl px-6 py-20 text-center">
                    <div className="uppercase-tag">Tarifs</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-5xl md:text-6xl">
                        Le zéro déchet, <span className="text-fzblue">accessible.</span>
                    </h1>
                    <p className="mt-4 text-zinc-600 max-w-xl mx-auto">
                        Gratuit à vie pour les 500 premiers. Passez Premium pour l'IA avancée, le scan photo et le mode famille.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 py-16 grid md:grid-cols-3 gap-6">
                <PlanCard name="Découverte" price="0€" period="/toujours"
                    testid="plan-free"
                    features={[
                        { ok: true, t: "Frigo + Congélateur illimités" },
                        { ok: true, t: "Alertes DLC intelligentes" },
                        { ok: true, t: "3 générations de recettes IA / jour" },
                        { ok: true, t: "Score hygiène" },
                        { ok: false, t: "Scan photo IA" },
                        { ok: false, t: "Mode famille (jusqu'à 6)" },
                    ]}
                    cta={<Link to="/register" className="btn-outline w-full justify-center">Commencer</Link>}
                />
                <PlanCard name="Premium" price="4,99€" period="/mois" highlighted
                    testid="plan-premium"
                    features={[
                        { ok: true, t: "Tout du plan Découverte" },
                        { ok: true, t: "Recettes IA illimitées" },
                        { ok: true, t: "Scan photo d'aliments" },
                        { ok: true, t: "Analyse nutritionnelle détaillée" },
                        { ok: true, t: "Mode famille jusqu'à 6 personnes" },
                        { ok: true, t: "Export liste de courses" },
                    ]}
                    cta={user ? (
                        <button data-testid="btn-upgrade" onClick={upgrade} disabled={loading} className="btn-primary w-full justify-center">
                            {loading ? "Redirection..." : "Passer Premium"} <ArrowRight size={16} />
                        </button>
                    ) : (
                        <Link to="/register" className="btn-primary w-full justify-center">Créer un compte</Link>
                    )}
                />
                <PlanCard name="Entreprise" price="Sur devis" period=""
                    testid="plan-business"
                    features={[
                        { ok: true, t: "Restaurants, cantines, hôtels" },
                        { ok: true, t: "API dédiée + intégrations" },
                        { ok: true, t: "Tableau de bord multi-sites" },
                        { ok: true, t: "Rapports RSE / bilan carbone" },
                        { ok: true, t: "SLA 99.9%" },
                        { ok: true, t: "Onboarding personnalisé" },
                    ]}
                    cta={<a href="mailto:hello@monfrigozero.app" className="btn-outline w-full justify-center">Nous contacter</a>}
                />
            </section>
            {msg && <div className="text-center text-fzred text-sm pb-6" data-testid="pricing-error">{msg}</div>}
        </div>
    );
}

function PlanCard({ name, price, period, features, cta, highlighted = false, testid }) {
    return (
        <div data-testid={testid} className={"p-8 border " + (highlighted ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white")}>
            <div className="flex items-center gap-2">
                <div className="uppercase-tag" style={highlighted ? { color: "#a1a1aa" } : {}}>{name}</div>
                {highlighted && <span className="text-[10px] font-mono px-2 py-0.5 bg-fzblue text-white">POPULAIRE</span>}
            </div>
            <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display font-black text-5xl tracking-tighter">{price}</span>
                <span className={"text-sm " + (highlighted ? "text-zinc-400" : "text-zinc-500")}>{period}</span>
            </div>
            <ul className="mt-8 space-y-3">
                {features.map((f) => (
                    <li key={f.t} className={"flex items-start gap-2 text-sm " + (f.ok ? "" : (highlighted ? "text-zinc-500 line-through" : "text-zinc-400 line-through"))}>
                        {f.ok ? <Check size={16} className="text-fzgreen mt-0.5" /> : <X size={16} className="mt-0.5" />}
                        <span>{f.t}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-8">{cta}</div>
        </div>
    );
}
